import React, {useCallback, useContext, useEffect, useState} from 'react';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import logo from '../assets/images/blueshineLogo.jpg';
import '../assets/styles/style.css'
import {useTranslation} from "react-i18next";
import useStateNavigation from "../services/use-state-navigation";
import {v4 as uuid} from "uuid";
import classNames from "classnames";
import {MyAppContext} from "../ui/AppContext";

export declare type LangItemBootstrapProps = {
    "data-bs-toggle": string;
    "data-bs-target": string;
}

export declare type LangItemNormalProps = {
    onClick: () => void;
}

export declare type LangItemProps = {
    className?: string;
    langKey: string;
    role?: string;
} & (LangItemNormalProps | LangItemBootstrapProps)

declare type NavLinkProps = {
    key: string;
    title: string;
    path?: string;
    className?: string[];
    iconCls?: string;
    onClick?: () => void;
    isShow?: boolean;
    submenu?: NavLinkProps[];
}

const MENU_ITEM_COM_INFO = {
    key: "companyInfo",
    title: "header:btn_company_info",
    path: "/company-info",
    iconCls: "logout"
}
const MENU_ITEM_COM_VISION = {
    key: "companyVision",
    title: "header:btn_company_vision",
    path: "/our-vision",
    iconCls: "logout"
}

const MENU_ITEM_OUR_SERVICE = {
    key: "service",
    title: "header:btn_our_service",
    path: "/service",
    iconCls: "logout"
}

const MENU_ITEM_NEWS_EVENT = {
    key: "news",
    title: "header:btn_news_event",
    path: "/news-event",
    iconCls: "logout"
}

const LangItem: React.FC<LangItemProps> = (props: LangItemProps) => {
    const {t, i18n} = useTranslation("header");
    const {langKey, ...rest} = props;
    const showLang = (): boolean => {
        return i18n.language === props.langKey.replace("lang_", "");
    }

    const getLangCode = (langKey: string) => {
        let langCode = 'en';
        switch (langKey) {
            case 'lang_zh-HK':
            case 'lang_zh-CN':
                langCode = langKey.split('lang_')[1];
                break;
            default:
                langCode = i18n.language;
                break;
        }
        return langCode;
    }

    return (
        <a {...rest} lang={getLangCode(props.langKey)} tabIndex={0} className={`langLink${showLang() ? " d-none" : ""}${props.className ? ` ${props.className}` : ''}`}
           onKeyPress={event => {
               if (event.key === 'Enter') {
                   event.currentTarget.click();
               }
           }}
           aria-label={t(props.langKey.includes('font_size') ? props.langKey : `full_${props.langKey}`)}>
            {t(props.langKey)}
        </a>
    )
};

const Header = () => {
    const {t} = useTranslation();
    const nav = useNavigate();
    const location = useLocation();
    const {deleteHashHistory} = useStateNavigation();
    const {setLang} = useContext(MyAppContext)
    const [showAboutList, setShowAboutList] = useState<boolean>(false);
    const [showServiceList, setShowServiceList] = useState<boolean>(false);
    const [showNewList, setShowNewList] = useState<boolean>(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);

    const [functionMenu, setFunctionMenu] = useState<NavLinkProps[]>([
        {
            key: "aboutus",
            title: "header:aboutUs",
            submenu: [MENU_ITEM_COM_INFO, MENU_ITEM_COM_VISION],
        },
        {
            key: "service",
            title: "header:service",
            submenu: [MENU_ITEM_OUR_SERVICE],
            // onClick: () => setShowAboutList(!showServiceList)
        },
        {
            key: "eventnews",
            title: "header:eventNew",
            submenu: [MENU_ITEM_NEWS_EVENT],
            // onClick: () => setShowAboutList(!showServiceList)
        }
    ]);

    //   const isNavLinkActive = (key) => {
    //   // Your existing active link logic
    //   return '';
    // };

    const onNavHeaderClick = (navLinkProps: NavLinkProps) => {
        if (navLinkProps.path) {
            nav(navLinkProps.path, {state: {pageId: uuid()}});
            deleteHashHistory(navLinkProps.path);
        } else if (navLinkProps.onClick) {
            navLinkProps.onClick();
        }
        setMobileMenuOpen(false);
    }

    const isNavLinkActive = useCallback((pathName: string): string => {
        const regexGroup = location.pathname.match(/^\/([\w-]+)\/?/);
        if (regexGroup) {
            return regexGroup[1] === pathName ? "active" : "";
        }

        return "";
    }, [location])

    const onLangClick = useCallback((lang: "en" | "zh-HK" | "zh-CN"): void => {
        setLang(lang);
    }, [setLang])

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const closeMobileMenu = () => {
        setMobileMenuOpen(false);
    };

    const toggleAboutUs = () => {
        setShowAboutList(!showAboutList)
    }

    const toggleService = () => {
        setShowServiceList(!showServiceList)
    }

    const toggleNew = () => {
        setShowNewList(!showNewList)
    }

    useEffect(() => {
        setFunctionMenu(prev => {
            return prev.map(item => {
                return item;
            })
        })
    }, []);

    return (
        <header className="header">
            <div id="navbar" className="nav flex justify-between bg-white items-center fixed top-0 w-full z-50 transition-all duration-300 shadow-sm">
                {/* Logo */}
                <div className="logo-container">
                    <Link to="/">
                        <img src={logo} alt="Company Logo" className=""/>
                    </Link>
                </div>

                {/* Desktop Navigation */}
                <nav className="hidden lg:block">
                    <ul className="flex items-center p4">
                        {
                            functionMenu
                                .filter(navLinkProps => navLinkProps.isShow ?? true)
                                .map(navLinkProps => {
                                    const isActive = activeSubmenu === navLinkProps.key;

                                    return (
                                        <li
                                            key={navLinkProps.key}
                                            className="nav-item relative mr-4 xl-custom-1440:mr-14 group"
                                            onMouseEnter={() => navLinkProps.submenu && setActiveSubmenu(navLinkProps.key)}
                                            onMouseLeave={() => navLinkProps.submenu && setActiveSubmenu(null)}
                                        >
                                            <div className="relative">
                                                <a
                                                    tabIndex={0}
                                                    className={classNames(
                                                        'nav-link',
                                                        'uppercase',
                                                        'cursor-pointer',
                                                        'text-primary',
                                                        'font-thin',
                                                        'hover:text-secondary',
                                                        'hover:font-bold',
                                                        'text-lg',
                                                        'xl:text-21',
                                                        isNavLinkActive(navLinkProps.key),
                                                        navLinkProps.className
                                                    )}
                                                    onClick={event => {
                                                        if (navLinkProps.submenu) {
                                                            setActiveSubmenu(isActive ? null : navLinkProps.key);
                                                            event.preventDefault();
                                                        } else {
                                                            onNavHeaderClick(navLinkProps);
                                                        }
                                                    }}
                                                >
                                                    {t(navLinkProps.title)}
                                                </a>

                                                {navLinkProps.submenu && (
                                                    <ul
                                                        className={`
                                                          absolute left-0 mt-2 w-48 bg-white shadow-lg py-1 z-20 hidden nav-item-content
                                                          ${isActive ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}
                                                          group-hover:opacity-100 group-hover:visible group-hover:translate-y-0
                                                        `}
                                                    >
                                                        {navLinkProps.submenu.map(subItem => (
                                                            <li key={subItem.key}>
                                                                <Link
                                                                    to={subItem.path!}
                                                                    className="block px-4 py-2 text-primary hover:bg-gray-100 hover:text-secondary"
                                                                    onClick={() => setActiveSubmenu(null)}
                                                                >
                                                                    {t(subItem.title)}
                                                                </Link>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })
                        }
                        <LangItem langKey={'EN'} onClick={() => onLangClick('en')}/>
                        <span className="mx-2">|</span>
                        {/*<LangItem langKey={'lang_zh-HK'} onClick={() => onLangClick('zh-HK')} />*/}
                        <LangItem langKey={'简'} onClick={() => onLangClick('zh-CN')}/>
                    </ul>
                </nav>

                {/* Desktop CTA Button */}
                <div className="nav-cta bg-secondary font-primary flex-none px-6 xl:px-10 hidden lg:block">
                    <a href="/contact-us" className="nav-cta-btn text-white text-base lg:text-lg font-bold flex justify-between items-center" aria-label="get in touch button">
                        <span className="cta-btn-txt relative mr-2.5">{t('header:btn_get_in_touch')}</span>
                        <span className="cta-btn-arr">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                        </svg>
                    </span>
                    </a>
                </div>

                {/*Mobile Menu Button */}
                <button className="bg-secondary text-white p-5 flex-none block lg:hidden hamburger-menu" type="button" aria-expanded="false"
                        onClick={toggleMobileMenu}
                >
                    <span aria-hidden="true"></span><span aria-hidden="true"></span><span aria-hidden="true"></span>
                </button>
            </div>

            {/*######################################################################################################################################################*/}
            {/*Mobile Menu */}
            {mobileMenuOpen &&
                <div className="mobile-menu has-fade lg:hidden py-4 z-30 bg-white font-primary fade-in">
                    <ul className="lg:hidden">
                        <li className="py-3 px-5 md:pl-10 mobile-menu-item" onClick={toggleAboutUs}>
                            <a role="button" aria-label="dropdown"
                               className="uppercase text-base text-primary flex justify-between items-center drop-down-menu hover:text-secondary hover:font-bold transition-all duration-300">
                                <span className="mobile-menu-link nav-link">{t('header:aboutUs')}</span>
                                <span className="mobile-menu-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                                    </svg>
                                </span>
                            </a>
                            {showAboutList &&
                                <div className="mobile-sub-menu mobile-sub-0 relative p-4">
                                    <ul className="lg:hidden">
                                        <li className="py-2">
                                            <a href="./company-info" className="text-base text-primary uppercase nav-link hover:text-secondary hover:font-bold transition-all duration-300">
                                                {t('header:btn_company_info')}
                                            </a>
                                        </li>
                                        <li className="py-2">
                                            <a href="./our-vision" className="text-base text-primary uppercase nav-link hover:text-secondary hover:font-bold transition-all duration-300">
                                                {t('header:btn_company_vision')}
                                            </a>
                                        </li>
                                    </ul>

                                </div>
                            }
                        </li>
                        <li className="py-3 px-5 md:pl-10 mobile-menu-item" onClick={toggleService}>
                            <a role="button" aria-label="dropdown"
                               className="uppercase text-base text-primary flex justify-between items-center drop-down-menu hover:text-secondary hover:font-bold transition-all duration-300">
                                <span className="mobile-menu-link nav-link">{t('header:service')}</span>
                                <span className="mobile-menu-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                                    </svg>
                                </span>
                            </a>
                            {showServiceList &&
                                (
                                    <div className="mobile-sub-menu mobile-sub-1 relative p-4">
                                        <ul className="lg:hidden">
                                            <li className="py-2">
                                                <a href="/service" className="text-base text-primary uppercase nav-link hover:text-secondary hover:font-bold transition-all duration-300">
                                                    {t('header:btn_our_service')}
                                                </a>
                                            </li>
                                        </ul>
                                    </div>
                                )
                            }
                        </li>

                        <li className="py-3 px-5 md:pl-10 mobile-menu-item" onClick={toggleNew}>
                            <a role="button" aria-label="dropdown"
                               className="uppercase text-base text-primary flex justify-between items-center drop-down-menu hover:text-secondary hover:font-bold transition-all duration-300">
                                <span className="mobile-menu-link nav-link">{t('header:eventNew')}</span>
                                <span className="mobile-menu-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                                    </svg>
                                </span>
                            </a>
                            {
                                showNewList &&
                                (
                                    <div className="mobile-sub-menu mobile-sub-2 relative p-4">
                                        <ul className="lg:hidden">
                                            <li className="py-2">
                                                <a href="/news-event"
                                                   className="text-base text-primary uppercase nav-link hover:text-secondary hover:font-bold transition-all duration-300">
                                                    {t('header:btn_news_event')}
                                                </a>
                                            </li>
                                        </ul>
                                    </div>
                                )
                            }
                        </li>

                        {/* language */}
                        <li className="px-5">
                            <div className="select-lang mr-4 xl-custom-1280:mr-6 flex">
                                <LangItem langKey={'EN'} onClick={() => onLangClick('en')}/>
                                <span className="mx-2">|</span>
                                {/*<LangItem langKey={'lang_zh-HK'} onClick={() => onLangClick('zh-HK')} />*/}
                                <LangItem langKey={'简'} onClick={() => onLangClick('zh-CN')}/>
                            </div>
                        </li>
                        <li className="py-3">
                            <a href="/contact-us" className="nav-cta-btn text-white bg-secondary py-3 px-4 text-base xl:text-lg font-bold flex justify-center items-center"
                               aria-label="get in touch button">
                                <span className="cta-btn-txt relative mr-1">{t('header:btn_get_in_touch')}</span>
                                <span className="cta-btn-arr">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                        </svg>
                    </span>
                            </a>
                        </li>
                    </ul>
                </div>
            }
        </header>
    );
};

export default Header;